import { AboutWhat } from "../components/AboutWhat";
import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API, formatApiError } from "../lib/api";
import { MODELS } from "../lib/models";
import { useAuth } from "../context/AuthContext";
import { KidFriendlyBadge } from "../components/KidFriendlyBadge";

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-xs font-mono uppercase tracking-[0.16em] text-slate-500">{label}</span>
    <input data-testid={testid} {...props} required={props.required ?? !testid.includes("guardian")} className="what-input mt-2" />
  </label>
);

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
  </svg>
);

export default function AuthPage() {
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  if (user) return <Navigate to={location.state?.from || "/"} replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = mode === "login" ? { email: form.email, password: form.password, guardian_code: form.guardian || undefined } : form;
      const { data } = await axios.post(`${API}/auth/${mode}`, body);
      setUser(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="what-app min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <section className="hidden lg:flex flex-col justify-between p-14 border-r border-white/[0.06] what-auth-hero">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-3xl font-extrabold tracking-tight text-white">WHAT?</span>
          <span className="what-dot" />
        </div>
        <div className="what-rise">
          <h1 data-testid="auth-hero-title" className="font-display text-5xl xl:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            <span className="block text-lg font-mono font-semibold tracking-[0.2em] uppercase text-[#D4FF3A] mb-5">Ask WHAT?</span>
            One question.<br /><span className="text-slate-500">Every AI's best thinking.</span>
          </h1>
          <div className="flex flex-wrap gap-2 mt-10">
            {MODELS.map((m) => (
              <span key={m.key} className="what-chip" style={{ "--c": m.color }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
                {m.name} <span className="opacity-60">{m.version}</span>
              </span>
            ))}
          </div>
          <div className="mt-6"><KidFriendlyBadge testid="auth-kid-friendly-badge" /></div>
          <div className="mt-8 max-w-lg"><AboutWhat testid="auth-about-what" /></div>
        </div>
        <p className="text-sm text-slate-500 font-mono">Your chats stay private to your account. <a href="/privacy" data-testid="auth-privacy-link" className="underline hover:text-white">Privacy Policy</a></p>
      </section>

      <section className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm what-rise">
          <p data-testid="auth-brand-mobile" className="lg:hidden font-display text-2xl font-extrabold text-white mb-8">Ask WHAT?<span className="block text-sm font-normal text-slate-400 mt-1">Three AIs debate. One answer you can trust.</span></p>
          <h2 data-testid="auth-title" className="font-display text-3xl font-bold text-white tracking-tight">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <div className="lg:hidden mt-4"><KidFriendlyBadge testid="auth-kid-friendly-badge-mobile" /></div>
          <p className="text-sm text-slate-400 mt-2">
            {mode === "login" ? "Log in to see your private chat history." : "Sign up to keep your own private history."}
          </p>

          <button data-testid="google-login-btn" onClick={googleLogin} className="what-btn-ghost w-full mt-8">
            <GoogleIcon /> Continue with Google
          </button>
          <div className="flex items-center gap-3 my-6 text-xs text-slate-600 font-mono">
            <span className="flex-1 h-px bg-white/[0.07]" /> or <span className="flex-1 h-px bg-white/[0.07]" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && <Field label="Name" testid="auth-name-input" value={form.name} onChange={set("name")} />}
            <Field label={mode === "login" ? "Email or student username" : "Email"} testid="auth-email-input" type={mode === "login" ? "text" : "email"} autoCapitalize="none" value={form.email} onChange={set("email")} />
            <Field label="Password" testid="auth-password-input" type="password" minLength={6} value={form.password} onChange={set("password")} />
            {mode === "login" && form.email && !form.email.includes("@") && (
              <div data-testid="guardian-code-field" className="rounded-xl border border-[#FBBF24]/40 bg-[#FBBF24]/5 p-3">
                <Field label="Parent or teacher code" testid="auth-guardian-code-input" type="password" inputMode="numeric" maxLength={6} value={form.guardian || ""} onChange={set("guardian")} />
                <p className="text-xs text-slate-400 mt-2">Children can't sign in on their own. A grown-up types their code (teacher code, or the parent's Kids Mode PIN).</p>
              </div>
            )}
            {mode === "login" && (
              <Link to="/forgot-password" data-testid="forgot-password-link" className="block text-xs text-slate-400 hover:text-[#D4FF3A] -mt-1">Forgot password?</Link>
            )}
            {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
            <button data-testid="auth-submit-btn" disabled={busy} className="what-btn-primary w-full !py-3">
              {busy && <Loader2 size={16} className="animate-spin" />}
              {mode === "login" ? "Log in" : "Create account"}
            </button>
          </form>

          <p className="text-sm text-slate-500 mt-6">
            {mode === "login" ? "New to WHAT?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle-mode-btn"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-[#D4FF3A] hover:underline"
            >
              {mode === "login" ? "Create an account" : "Log in"}
            </button>
          </p>
          <p className="text-xs text-slate-600 mt-4 lg:hidden">
            <a href="/privacy" data-testid="auth-privacy-link-mobile" className="underline hover:text-slate-300">Privacy Policy</a>
          </p>
        </div>
      </section>
    </div>
  );
}
